// =============================================================================
// MEMORY ALBUM SELECTOR
// =============================================================================
// Modal for selecting an album to share during a call

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMemorySessionStore } from '../../stores/memorySessionStore.js';
import { X, Loader2, Images, Play } from '../ui/Icons.js';
import { withAuthToken } from '../../api/client.js';

interface MemoryAlbumSelectorProps {
  callId: string;
  // The call's conversation - used to compile the auto recap montage from
  // everything this conversation has shared.
  conversationId: string;
  onClose: () => void;
}

// Map a memory:start-failed error code to friendly copy. Any other error
// string (network, server) falls through to a generic message.
function emptyMessage(error: string, t: (key: string, fallback: string) => string): string | null {
  switch (error) {
    case 'memory:empty:album':
      return t('memory.emptyAlbum', 'This album has no photos to show yet. Add a few photos, then try again.');
    case 'memory:empty:recap':
      return t('memory.emptyRecap', 'There are no shared photos to recap yet. Share some photos in this chat, then try again.');
    case 'memory:empty:date':
      return t('memory.emptyDate', 'No photos were shared on this day in earlier years yet.');
    case 'memory:empty:selection':
      return t('memory.emptySelection', 'None of the chosen photos could be shown.');
    default:
      return null;
  }
}

export function MemoryAlbumSelector({ callId, conversationId, onClose }: MemoryAlbumSelectorProps) {
  const { t } = useTranslation();
  const { albums, isLoading, error, loadAlbums, startSession, startRecapSession, clearError } =
    useMemorySessionStore();

  // Load albums on mount
  useEffect(() => {
    void loadAlbums();
  }, [loadAlbums]);

  // A fresh emit always clears any prior empty/error state so the picker does
  // not show stale copy alongside a new attempt. The session view takes over
  // automatically once memory:started arrives (ActiveCallView swaps in
  // MemorySessionView), so there is nothing to close here on success - an empty
  // source comes back as memory:start-failed and is shown below.
  const handleSelectAlbum = (albumId: string) => {
    clearError();
    void startSession(callId, albumId);
  };

  const handlePlayRecap = () => {
    clearError();
    void startRecapSession(callId, conversationId);
  };

  const friendlyEmpty = error ? emptyMessage(error, t) : null;

  return (
    <div className="fixed inset-0 z-60 flex items-center justify-center bg-ink-1/80 backdrop-blur-sm">
      <div className="relative mx-4 max-h-[80vh] w-full max-w-lg overflow-hidden rounded-2xl bg-surface-2 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-line-2 px-6 py-4">
          <div>
            <h2 className="text-xl font-bold text-white">
              {t('memory.selectAlbum', 'Share Photos')}
            </h2>
            <p className="mt-1 text-sm text-ink-3">
              {t('memory.selectAlbumDescription', 'Choose an album to view together')}
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-full p-2 text-ink-3 transition-colors hover:bg-surface-sunken hover:text-ink-1"
            aria-label={t('common.close', 'Close')}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="max-h-[60vh] overflow-y-auto p-6">
          {/* Loading */}
          {isLoading && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="mt-2 text-ink-3">
                {t('common.loading', 'Loading...')}
              </p>
            </div>
          )}

          {/* Empty / failed start - friendly recovery, never a dead end */}
          {!isLoading && friendlyEmpty && (
            <div className="mb-4 rounded-lg border border-line-2 bg-surface-3 p-4 text-sm text-ink-2" role="status">
              <p>{friendlyEmpty}</p>
              <button
                onClick={clearError}
                className="mt-2 min-h-[44px] rounded-lg bg-primary px-4 py-2 font-medium text-primary-on transition-colors hover:bg-primary-hover"
              >
                {t('common.ok', 'OK')}
              </button>
            </div>
          )}

          {/* Other errors (network, server) */}
          {!isLoading && error && !friendlyEmpty && (
            <div className="mb-4 rounded-lg bg-danger-soft p-4 text-danger">
              {error}
              <button
                onClick={clearError}
                className="ml-2 underline hover:no-underline"
              >
                {t('common.dismiss', 'Dismiss')}
              </button>
            </div>
          )}

          {/* Auto recap montage - always offered; an empty history recovers
              gracefully via the start-failed message above. */}
          {!isLoading && (
            <button
              onClick={handlePlayRecap}
              className="mb-4 flex w-full items-center gap-3 rounded-xl bg-primary/15 p-4 text-left transition-colors hover:bg-primary/25 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
              data-testid="play-recap-button"
            >
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary text-primary-on">
                <Play className="h-6 w-6" />
              </span>
              <span>
                <span className="block font-medium text-white">
                  {t('memory.playRecap', 'Play recap montage')}
                </span>
                <span className="block text-sm text-ink-3">
                  {t('memory.playRecapDescription', 'Look back through everything you have shared')}
                </span>
              </span>
            </button>
          )}

          {/* Albums empty state - a recap is still available above, so this is
              no longer a dead end. */}
          {!isLoading && albums.length === 0 && (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <Images className="mb-4 h-16 w-16 text-ink-4" />
              <h3 className="text-lg font-medium text-white">
                {t('memory.noAlbums', 'No Albums')}
              </h3>
              <p className="mt-1 text-ink-3">
                {t(
                  'memory.noAlbumsDescription',
                  'Create an album first to share photos during calls'
                )}
              </p>
            </div>
          )}

          {/* Album grid */}
          {!isLoading && albums.length > 0 && (
            <div className="grid grid-cols-2 gap-4">
              {albums.map((album) => (
                <button
                  key={album.id}
                  onClick={() => { handleSelectAlbum(album.id); }}
                  className="group relative overflow-hidden rounded-xl bg-surface-sunken transition-all hover:ring-2 hover:ring-primary"
                >
                  {/* Cover image */}
                  <div className="aspect-square">
                    {album.coverPhotoUrl ? (
                      <img
                        src={withAuthToken(album.coverPhotoUrl)}
                        alt={album.name}
                        className="h-full w-full object-cover transition-transform group-hover:scale-105"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center bg-surface-3">
                        <Images className="h-12 w-12 text-ink-3" />
                      </div>
                    )}
                  </div>

                  {/* Album info overlay */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-ink-1 to-transparent p-3 pt-8">
                    <p className="font-medium text-white">{album.name}</p>
                    <p className="text-sm text-ink-3">
                      {t('memory.photoCount', '{{count}} photos', {
                        count: album.photoCount,
                      })}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer hint */}
        <div className="border-t border-line-2 px-6 py-3 text-center text-sm text-ink-3">
          {t(
            'memory.bothCanControl',
            'Both participants can control the slideshow'
          )}
        </div>
      </div>
    </div>
  );
}
